"use client"

import { useEffect, useState } from "react"

// Client side of Patreon mode (see lib/patreon-mode.ts). One shared request for the whole page, refreshed at most once a minute.
// While loading, or if the request fails, it reports OFF (the normal app); the server routes are what really enforce the lock.
type Mode = { enabled: boolean; pageUrl: string | null }
let cached: { at: number; value: Mode } | null = null
let inflight: Promise<Mode> | null = null
const CACHE_MS = 60_000
const OFF: Mode = { enabled: false, pageUrl: null }

function fetchMode(): Promise<Mode> {
  if (cached && Date.now() - cached.at < CACHE_MS) return Promise.resolve(cached.value)
  if (inflight) return inflight
  inflight = fetch("/api/patreon-mode", { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : OFF))
    .then((j) => {
      const value: Mode = { enabled: j?.enabled === true, pageUrl: typeof j?.pageUrl === "string" ? j.pageUrl : null }
      cached = { at: Date.now(), value }
      return value
    })
    .catch(() => OFF)
    .finally(() => {
      inflight = null
    })
  return inflight
}

export function clearPatreonModeClientCache() {
  cached = null
}

export function usePatreonMode(): { enabled: boolean; loading: boolean; pageUrl: string | null } {
  const [mode, setMode] = useState<Mode>(cached?.value ?? OFF)
  const [loading, setLoading] = useState(cached === null)
  useEffect(() => {
    let alive = true
    fetchMode().then((v) => {
      if (!alive) return
      setMode(v)
      setLoading(false)
    })
    return () => {
      alive = false
    }
  }, [])
  return { enabled: mode.enabled, loading, pageUrl: mode.pageUrl }
}
